import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { Building2, ArrowRight, Loader2, Sparkles, Globe } from 'lucide-react';
import creditReportMoneyLogo from '@/assets/credit-report-money-logo.png';

const orgSchema = z.object({
  name: z.string().min(2, 'Organization name must be at least 2 characters'),
  slug: z.string()
    .min(3, 'Slug must be at least 3 characters')
    .regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
});

export default function Onboarding() {
  const [orgName, setOrgName] = useState('');
  const [slug, setSlug] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; slug?: string }>({});
  
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    const generatedSlug = orgName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .slice(0, 50);
    setSlug(generatedSlug);
  }, [orgName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    const validation = orgSchema.safeParse({ name: orgName, slug });
    if (!validation.success) {
      const fieldErrors: { name?: string; slug?: string } = {};
      validation.error.errors.forEach((err) => {
        if (err.path[0] === 'name') fieldErrors.name = err.message;
        if (err.path[0] === 'slug') fieldErrors.slug = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    if (!user) return;

    setIsLoading(true);

    try {
      const { data: org, error: orgError } = await supabase
        .from('organizations')
        .insert({
          name: orgName,
          slug: slug,
        })
        .select()
        .single();

      if (orgError) {
        if (orgError.message.includes('duplicate key') || orgError.message.includes('unique constraint')) {
          toast({
            title: 'Slug Already Taken',
            description: 'This organization URL is already in use. Please choose a different one.',
            variant: 'destructive',
          });
        } else {
          throw orgError;
        }
        return;
      }

      const { error: memberError } = await supabase
        .from('organization_members')
        .insert({
          organization_id: org.id,
          user_id: user.id,
          role: 'owner',
        });

      if (memberError) throw memberError;

      const { error: creditsError } = await supabase
        .from('scan_credits')
        .insert({
          organization_id: org.id,
          amount: 10,
          description: 'Welcome bonus - 10 free scans',
        });

      if (creditsError) throw creditsError;

      toast({
        title: 'Organization created!',
        description: 'Your organization is ready. Enjoy your 10 free scans!',
      });

      navigate('/dashboard');
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create organization',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center relative overflow-hidden">
        <div className="ambient-orb ambient-orb-primary absolute -top-[20%] -left-[10%] w-[50%] h-[50%]" />
        <div className="ambient-orb ambient-orb-secondary absolute bottom-[10%] -right-[10%] w-[40%] h-[40%]" />
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6 py-12 relative overflow-hidden">
      {/* Ambient Background */}
      <div className="fixed inset-0 pointer-events-none -z-10">
        <div className="ambient-orb ambient-orb-primary absolute -top-[20%] -left-[10%] w-[50%] h-[50%]" />
        <div className="ambient-orb ambient-orb-secondary absolute bottom-[20%] -right-[10%] w-[40%] h-[40%]" />
        <div className="absolute inset-0 radar-grid opacity-20" />
      </div>

      <div className="w-full max-w-md relative z-10 animate-reveal">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <Link to="/">
            <img 
              src={creditReportMoneyLogo} 
              alt="Credit Report Money" 
              className="h-16 w-auto"
            />
          </Link>
        </div>

        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 mb-4">
            <Building2 className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold mb-2">
            <span className="gradient-text-gold">Set up your organization</span>
          </h1>
          <p className="text-muted-foreground">
            Create your workspace to start analyzing credit reports
          </p>
        </div>

        <div className="glass-panel-strong rounded-2xl p-8 border border-border/50">
          <div className="flex items-center gap-2 text-accent mb-6 justify-center glass-panel rounded-xl py-3 px-4 border border-accent/20">
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-semibold">10 free scans included!</span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="orgName">Organization Name</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="orgName"
                  type="text"
                  placeholder="Acme Credit Repair"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  className="pl-10 bg-card/50 border-border/50 focus:border-primary/50"
                  disabled={isLoading}
                />
              </div>
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Organization URL</Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="slug"
                  type="text"
                  placeholder="acme-credit"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                  className="pl-10 bg-card/50 border-border/50 focus:border-primary/50"
                  disabled={isLoading}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                app.creditreportmoney.com/<span className="text-primary">{slug || 'your-org'}</span>
              </p>
              {errors.slug && (
                <p className="text-sm text-destructive">{errors.slug}</p>
              )}
            </div>

            <Button 
              type="submit" 
              className="w-full btn-glow bg-gradient-to-r from-primary to-primary/90" 
              size="lg"
              disabled={isLoading}
            >
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <ArrowRight className="h-4 w-4 mr-2" />
              )}
              Create Organization
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
